import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Stack,
  LinearProgress,
  Tooltip,
  useTheme,
  alpha,
  keyframes,
  Fade,
  Paper
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  Timeline as TimelineIcon,
  EmojiEvents,
  LocalFireDepartment,
  Favorite,
  Stars,
  Celebration,
  Flag,
  DirectionsRun,
  FitnessCenter,
  Restaurant,
  MonitorWeight,
  Assignment,
  EventAvailable,
  TrendingUp,
  Groups,
  Schedule,
  AutoAwesome,
  WorkspacePremium,
  MilitaryTech,
  School
} from '@mui/icons-material';
import { formatDateSafe } from '../../shared/utils/dateHelpers';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 215, 0, 0.6);
  }
  100% {
    box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
  }
`;

interface Milestone {
  date: Date | null;
  label: string;
  color: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'grey';
  show: boolean;
  icon?: React.ReactNode;
  description?: string;
}

interface MilestoneCategory {
  id: string;
  name: string;
  icon: React.ReactElement;
  color: string;
  milestones: EnhancedMilestone[];
}

interface EnhancedMilestone extends Milestone {
  id: string;
  category: string;
  achievement?: {
    badge: React.ReactElement;
    title: string;
    points: number;
  };
  status: 'completed' | 'upcoming' | 'overdue';
}

interface ClientJourneyMilestonesProps {
  milestones: Milestone[];
  client: any;
}

const ClientJourneyMilestones: React.FC<ClientJourneyMilestonesProps> = ({ milestones, client }) => {
  const theme = useTheme();
  const [expandedCategory, setExpandedCategory] = useState<string | null>('all');

  // Helper function to determine if a check-in or NPS milestone is actually completed
  const isMilestoneActuallyCompleted = (milestone: Milestone): boolean => {
    // For check-ins and NPS, we need to verify actual submission
    if (milestone.label.includes('Check-in') || milestone.label.includes('NPS')) {
      // Check if the milestone has a color of 'success' which indicates completion
      // The parent component sets this based on actual submission data
      return milestone.color === 'success';
    }
    
    // For other milestones, check if the date has passed
    return milestone.date ? new Date(milestone.date) <= new Date() : false;
  };

  // Enhance milestones with additional data
  const enhanceMilestones = (): MilestoneCategory[] => {
    const enhancedMilestones: EnhancedMilestone[] = milestones.map((milestone, index) => {
      const isCompleted = isMilestoneActuallyCompleted(milestone);
      const category = determineMilestoneCategory(milestone.label);
      
      return {
        ...milestone,
        id: `milestone-${index}`,
        category,
        status: isCompleted ? 'completed' : 'upcoming',
        icon: getMilestoneIcon(milestone.label),
        description: getMilestoneDescription(milestone.label),
        achievement: getMilestoneAchievement(milestone.label, isCompleted)
      };
    });

    // Group by category
    const categories: MilestoneCategory[] = [
      {
        id: 'onboarding',
        name: 'Onboarding Journey',
        icon: <Flag />,
        color: theme.palette.primary.main,
        milestones: enhancedMilestones.filter(m => m.category === 'onboarding')
      },
      {
        id: 'assessments',
        name: 'Health & Fitness Assessments',
        icon: <Assignment />,
        color: theme.palette.secondary.main,
        milestones: enhancedMilestones.filter(m => m.category === 'assessments')
      },
      {
        id: 'progress',
        name: 'Progress Checkpoints',
        icon: <TrendingUp />,
        color: theme.palette.success.main,
        milestones: enhancedMilestones.filter(m => m.category === 'progress')
      },
      {
        id: 'achievements',
        name: 'Special Achievements',
        icon: <EmojiEvents />,
        color: theme.palette.warning.main,
        milestones: enhancedMilestones.filter(m => m.category === 'achievements')
      }
    ];

    return categories.filter(cat => cat.milestones.length > 0);
  };

  const determineMilestoneCategory = (label: string): string => {
    if (label.includes('Joined') || label.includes('Health Consult') || label.includes('Started')) {
      return 'onboarding';
    } else if (label.includes('Assessment') || label.includes('Screening')) {
      return 'assessments';
    } else if (label.includes('Check-in') || label.includes('Attendance') || label.includes('NPS')) {
      return 'progress';
    } else if (label.includes('Sessions Attended')) {
      return 'achievements';
    } else {
      return 'achievements';
    }
  };

  const getMilestoneIcon = (label: string): React.ReactElement => {
    if (label.includes('Joined UFIT')) return <Groups />;
    if (label.includes('Health Consult')) return <Favorite />;
    if (label.includes('Programme Started')) return <Flag />;
    if (label.includes('First Attendance')) return <DirectionsRun />;
    if (label.includes('Metabolic')) return <LocalFireDepartment />;
    if (label.includes('Performance') || label.includes('Movement')) return <FitnessCenter />;
    if (label.includes('Nutrition')) return <Restaurant />;
    if (label.includes('Inbody')) return <MonitorWeight />;
    if (label.includes('Check-in')) return <EventAvailable />;
    if (label.includes('Sessions Attended')) return <EmojiEvents />;
    if (label.includes('NPS')) return <Stars />;
    return <Stars />;
  };

  const getMilestoneDescription = (label: string): string => {
    const descriptions: { [key: string]: string } = {
      'Joined UFIT': 'Welcome to the UFIT family! Your health transformation journey begins here.',
      'Health Consult': 'Comprehensive health evaluation to understand your clients unique needs and goals.',
      'Programme Started': 'Custom-designed health and fitness programme launched based on your clients assessment results.',
      'First Attendance': 'The first attendance marks the start of your clients journey.',
      'Initial Metabolic Assessment': 'Baseline metabolic rate measured to optimize your clients nutrition plan.',
      'Initial Performance Screening': 'Movement quality and performance capabilities evaluated.',
      'Initial Nutrition Assessment': 'Personalized nutrition strategy developed for your clients goals.',
      '2-Week Check-in': 'Early progress review to ensure your clients are on the right track.',
      '6-Week NPS': 'Net Promoter Score survey to measure client satisfaction and gather feedback.',
      '10-Week Check-in': 'Progress assessment to celebrate achievements and refine your clients goals.',
      '300 Sessions Attended': 'Triple century legend! 300 sessions of unstoppable progress.',
      '275 Sessions Attended': 'Elite achievement! 275 sessions of consistent excellence.',
      '250 Sessions Attended': 'Quarter-thousand milestone! Your journey is truly exceptional.',
      '225 Sessions Attended': 'Legendary status! 225 sessions of pushing boundaries.',
      '200 Sessions Attended': 'Double century! 200 sessions of excellence and transformation.',
      '175 Sessions Attended': 'Remarkable achievement! 175 sessions of unwavering commitment.',
      '150 Sessions Attended': 'Exceptional consistency! 150 sessions of building a stronger you.',
      '125 Sessions Attended': 'Outstanding dedication! 125 sessions completed on your journey.',
      '100 Sessions Attended': 'Century milestone achieved! 100 sessions of transforming your clients fitness.',
      '75 Sessions Attended': 'Three-quarters of a century! Your clients commitment is truly inspiring.',
      '50 Sessions Attended': 'Half-century milestone! 50 sessions of consistent effort and growth.',
      '25 Sessions Attended': 'Congratulations on completing 25 sessions! Your clients dedication is paying off.'
    };
    
    // Check for exact match first
    if (descriptions[label]) {
      return descriptions[label];
    }
    
    // Then check for partial matches
    for (const [key, desc] of Object.entries(descriptions)) {
      if (label.includes(key)) return desc;
    }
    return 'Another step forward in your fitness journey!';
  };

  const getMilestoneAchievement = (label: string, isCompleted: boolean | null): EnhancedMilestone['achievement'] | undefined => {
    if (!isCompleted) return undefined;

    const achievements: { [key: string]: EnhancedMilestone['achievement'] } = {
      'Joined UFIT': {
        badge: <WorkspacePremium sx={{ color: '#FFD700' }} />,
        title: 'UFIT Member',
        points: 100
      },
      'First Attendance': {
        badge: <MilitaryTech sx={{ color: '#C0C0C0' }} />,
        title: 'First Steps',
        points: 50
      },
      'Programme Started': {
        badge: <School sx={{ color: '#CD7F32' }} />,
        title: 'Program Initiated',
        points: 75
      },
      '10-Week Check-in': {
        badge: <EmojiEvents sx={{ color: '#FFD700' }} />,
        title: '10-Week Champion',
        points: 200
      },
      '200 Sessions Attended': {
        badge: <Stars sx={{ color: '#FFD700' }} />,
        title: 'Double Century Legend',
        points: 1000
      },
      '150 Sessions Attended': {
        badge: <WorkspacePremium sx={{ color: '#FFD700' }} />,
        title: 'Elite Member',
        points: 750
      },
      '100 Sessions Attended': {
        badge: <EmojiEvents sx={{ color: '#FFD700' }} />,
        title: 'Century Club',
        points: 500
      },
      '75 Sessions Attended': {
        badge: <MilitaryTech sx={{ color: '#FFD700' }} />,
        title: 'Diamond Dedication',
        points: 350
      },
      '50 Sessions Attended': {
        badge: <MilitaryTech sx={{ color: '#C0C0C0' }} />,
        title: 'Half Century',
        points: 250
      },
      '25 Sessions Attended': {
        badge: <MilitaryTech sx={{ color: '#CD7F32' }} />,
        title: 'Quarter Century',
        points: 150
      }
    };

    // Check for exact match first
    if (achievements[label]) {
      return achievements[label];
    }

    // Then check for partial matches
    for (const [key, achievement] of Object.entries(achievements)) {
      if (label.includes(key)) return achievement;
    }
    return undefined;
  };

  const categories = enhanceMilestones();
  const totalMilestones = milestones.length;
  const completedMilestones = milestones.filter(m => isMilestoneActuallyCompleted(m)).length;
  const progressPercentage = (completedMilestones / totalMilestones) * 100;

  // Calculate next milestone
  const upcomingMilestones = milestones
    .filter(m => m.date && new Date(m.date) > new Date())
    .sort((a, b) => (a.date?.getTime() || 0) - (b.date?.getTime() || 0));
  const nextMilestone = upcomingMilestones[0];

  return (
    <Card elevation={2} sx={{ borderRadius: 3, overflow: 'visible' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
            <TimelineIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
            Fitness Journey Milestones
          </Typography>
          
          {/* Progress Overview */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 2,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
              borderRadius: 2
            }}
          >
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Celebration sx={{ mr: 1, color: 'secondary.main' }} />
                  Journey Progress
                </Typography>
                <Chip
                  label={`${completedMilestones} of ${totalMilestones} Completed`}
                  color="primary"
                  icon={<CheckCircle />}
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Completion
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.round(progressPercentage)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 12,
                    borderRadius: 6,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 6,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      animation: `${shimmer} 2s ease-in-out infinite`
                    }
                  }}
                />
              </Box>

              {nextMilestone && (
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    background: alpha(theme.palette.warning.light, 0.1),
                    borderLeft: `4px solid ${theme.palette.warning.main}`
                  }}
                >
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Schedule sx={{ mr: 1, fontSize: 18 }} />
                    Next Milestone
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {nextMilestone.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Scheduled for {formatDateSafe(nextMilestone.date, 'MMMM d, yyyy')}
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Paper>
        </Box>

        {/* Category Tabs */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, overflowX: 'auto', pb: 1 }}>
          <Chip
            label="All Milestones"
            onClick={() => setExpandedCategory('all')}
            color={expandedCategory === 'all' ? 'primary' : 'default'}
            icon={<AutoAwesome />}
            sx={{
              cursor: 'pointer',
              fontWeight: expandedCategory === 'all' ? 'bold' : 'normal'
            }}
          />
          {categories.map(category => (
            <Chip
              key={category.id}
              label={category.name}
              onClick={() => setExpandedCategory(category.id)}
              color={expandedCategory === category.id ? 'primary' : 'default'}
              icon={category.icon}
              sx={{
                cursor: 'pointer',
                fontWeight: expandedCategory === category.id ? 'bold' : 'normal'
              }}
            />
          ))}
        </Stack>

        {/* Milestones Display */}
        <Box>
          {expandedCategory === 'all' ? (
            // Show all milestones in a timeline view
            <Box sx={{ position: 'relative' }}>
              {milestones.map((milestone, index) => {
                const enhanced = categories
                  .flatMap(c => c.milestones)
                  .find(m => m.label === milestone.label);
                const isCompleted = isMilestoneActuallyCompleted(milestone);
                const isLast = index === milestones.length - 1;

                return (
                  <Fade in timeout={300 * (index + 1)} key={index}>
                    <Box sx={{ display: 'flex', mb: isLast ? 0 : 3 }}>
                      {/* Timeline Line */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 3 }}>
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            backgroundColor: isCompleted 
                              ? (milestone.color === 'primary' ? theme.palette.primary.main :
                                 milestone.color === 'secondary' ? theme.palette.secondary.main :
                                 milestone.color === 'success' ? theme.palette.success.main :
                                 milestone.color === 'warning' ? theme.palette.warning.main :
                                 milestone.color === 'info' ? theme.palette.info.main :
                                 theme.palette.grey[500])
                              : alpha(milestone.color === 'primary' ? theme.palette.primary.main :
                                      milestone.color === 'secondary' ? theme.palette.secondary.main :
                                      milestone.color === 'success' ? theme.palette.success.main :
                                      milestone.color === 'warning' ? theme.palette.warning.main :
                                      milestone.color === 'info' ? theme.palette.info.main :
                                      theme.palette.grey[500], 0.2),
                            border: `3px solid ${theme.palette.background.paper}`,
                            boxShadow: isCompleted ? theme.shadows[3] : 'none',
                            animation: enhanced?.achievement ? `${glow} 2s ease-in-out infinite` : 'none'
                          }}
                        >
                          {enhanced?.icon || <CheckCircle />}
                        </Avatar>
                        {!isLast && (
                          <Box
                            sx={{
                              width: 2,
                              flexGrow: 1,
                              backgroundColor: isCompleted 
                                ? (milestone.color === 'primary' ? theme.palette.primary.main :
                                   milestone.color === 'secondary' ? theme.palette.secondary.main :
                                   milestone.color === 'success' ? theme.palette.success.main :
                                   milestone.color === 'warning' ? theme.palette.warning.main :
                                   milestone.color === 'info' ? theme.palette.info.main :
                                   theme.palette.grey[500])
                                : alpha(theme.palette.grey[400], 0.3),
                              mt: 1
                            }}
                          />
                        )}
                      </Box>

                      {/* Milestone Card */}
                      <Paper
                        elevation={isCompleted ? 3 : 1}
                        sx={{
                          flexGrow: 1,
                          p: 2.5,
                          borderRadius: 2,
                          backgroundColor: isCompleted 
                            ? alpha(milestone.color === 'primary' ? theme.palette.primary.main :
                                    milestone.color === 'secondary' ? theme.palette.secondary.main :
                                    milestone.color === 'success' ? theme.palette.success.main :
                                    milestone.color === 'warning' ? theme.palette.warning.main :
                                    milestone.color === 'info' ? theme.palette.info.main :
                                    theme.palette.grey[500], 0.05)
                            : alpha(theme.palette.grey[100], 0.5),
                          borderLeft: `4px solid ${
                            milestone.color === 'primary' ? theme.palette.primary.main :
                            milestone.color === 'secondary' ? theme.palette.secondary.main :
                            milestone.color === 'success' ? theme.palette.success.main :
                            milestone.color === 'warning' ? theme.palette.warning.main :
                            milestone.color === 'info' ? theme.palette.info.main :
                            theme.palette.grey[500]
                          }`,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateX(5px)',
                            boxShadow: theme.shadows[6]
                          }
                        }}
                        onClick={() => {}}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: isCompleted ? 'bold' : 'normal' }}>
                              {milestone.label}
                              {enhanced?.achievement && (
                                <Tooltip title={`${enhanced.achievement.title} - ${enhanced.achievement.points} points`}>
                                  <Box component="span" sx={{ ml: 1, display: 'inline-flex', animation: `${bounce} 2s ease-in-out infinite` }}>
                                    {enhanced.achievement.badge}
                                  </Box>
                                </Tooltip>
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {enhanced?.description}
                            </Typography>
                            <Typography variant="caption" color={isCompleted ? 'success.main' : 'text.secondary'}>
                              {isCompleted ? 'Completed' : 
                               (milestone.label.includes('Check-in') || milestone.label.includes('NPS')) && milestone.date && new Date(milestone.date) <= new Date() ? 'Pending Submission' : 'Scheduled'
                              } • {formatDateSafe(milestone.date, 'MMMM d, yyyy')}
                            </Typography>
                          </Box>
                          {isCompleted ? (
                            <CheckCircle sx={{ color: 'success.main', ml: 2 }} />
                          ) : (
                            (milestone.label.includes('Check-in') || milestone.label.includes('NPS')) && milestone.date && new Date(milestone.date) <= new Date() && (
                              <Schedule sx={{ color: 'warning.main', ml: 2 }} />
                            )
                          )}
                        </Box>
                      </Paper>
                    </Box>
                  </Fade>
                );
              })}
            </Box>
          ) : (
            // Show specific category milestones
            <Box>
              {categories
                .filter(cat => cat.id === expandedCategory)
                .map(category => (
                  <Box key={category.id}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: category.color }}>
                      {category.icon}
                      <Box component="span" sx={{ ml: 1 }}>{category.name}</Box>
                    </Typography>
                    <Stack spacing={2}>
                      {category.milestones.map((milestone, index) => {
                        const isCompleted = milestone.status === 'completed';
                        
                        return (
                          <Fade in timeout={300 * (index + 1)} key={milestone.id}>
                            <Paper
                              elevation={isCompleted ? 2 : 1}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                backgroundColor: isCompleted 
                                  ? alpha(milestone.color === 'primary' ? theme.palette.primary.main :
                                          milestone.color === 'secondary' ? theme.palette.secondary.main :
                                          milestone.color === 'success' ? theme.palette.success.main :
                                          milestone.color === 'warning' ? theme.palette.warning.main :
                                          milestone.color === 'info' ? theme.palette.info.main :
                                          theme.palette.grey[500], 0.05)
                                  : 'background.paper',
                                borderLeft: `4px solid ${
                                  milestone.color === 'primary' ? theme.palette.primary.main :
                                  milestone.color === 'secondary' ? theme.palette.secondary.main :
                                  milestone.color === 'success' ? theme.palette.success.main :
                                  milestone.color === 'warning' ? theme.palette.warning.main :
                                  milestone.color === 'info' ? theme.palette.info.main :
                                  theme.palette.grey[500]
                                }`,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: theme.shadows[4]
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar
                                  sx={{
                                    width: 40,
                                    height: 40,
                                    backgroundColor: isCompleted 
                                      ? (milestone.color === 'primary' ? theme.palette.primary.main :
                                         milestone.color === 'secondary' ? theme.palette.secondary.main :
                                         milestone.color === 'success' ? theme.palette.success.main :
                                         milestone.color === 'warning' ? theme.palette.warning.main :
                                         milestone.color === 'info' ? theme.palette.info.main :
                                         theme.palette.grey[500])
                                      : alpha(milestone.color === 'primary' ? theme.palette.primary.main :
                                              milestone.color === 'secondary' ? theme.palette.secondary.main :
                                              milestone.color === 'success' ? theme.palette.success.main :
                                              milestone.color === 'warning' ? theme.palette.warning.main :
                                              milestone.color === 'info' ? theme.palette.info.main :
                                              theme.palette.grey[500], 0.2),
                                    mr: 2
                                  }}
                                >
                                  {milestone.icon}
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="subtitle1" fontWeight={isCompleted ? 'bold' : 'normal'}>
                                    {milestone.label}
                                    {milestone.achievement && (
                                      <Box component="span" sx={{ ml: 1 }}>
                                        {milestone.achievement.badge}
                                      </Box>
                                    )}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateSafe(milestone.date, 'MMMM d, yyyy')}
                                  </Typography>
                                </Box>
                                {isCompleted ? (
                                  <CheckCircle sx={{ color: 'success.main' }} />
                                ) : (
                                  (milestone.label.includes('Check-in') || milestone.label.includes('NPS')) && milestone.date && new Date(milestone.date) <= new Date() ? (
                                    <Schedule sx={{ color: 'warning.main' }} />
                                  ) : (
                                    <RadioButtonUnchecked sx={{ color: 'grey.400' }} />
                                  )
                                )}
                              </Box>
                            </Paper>
                          </Fade>
                        );
                      })}
                    </Stack>
                  </Box>
                ))}
            </Box>
          )}
        </Box>

        {/* Achievement Summary
        {completedMilestones > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mt: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
              <EmojiEvents sx={{ mr: 1, color: 'warning.main' }} />
              Achievement Points Earned
            </Typography>
            <Typography variant="h3" fontWeight="bold" color="warning.main">
              {categories
                .flatMap(c => c.milestones)
                .filter(m => m.achievement && m.status === 'completed')
                .reduce((sum, m) => sum + (m.achievement?.points || 0), 0)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Keep pushing forward to unlock more achievements!
            </Typography>
          </Paper>
        )} */}
      </CardContent>
    </Card>
  );
};

export default ClientJourneyMilestones;